import { getSelectList } from 'axios/dictionaryApi';
import {
    getCoverGroupList,
    getParentServiceList,
    getProceduresDefList,
    getServiceList,
} from 'axios/tariffsApi';
import useApi from 'hooks/useApi';
import { createContext, ReactNode, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { DropDown, useErrorState } from '@finbeat/react-toolkit';

type Dicts = {
    termUnits: DropDown[];
    serviceTypes: DropDown[];
    calcTypes: DropDown[];
};

type TariffInfoContextType = {
    dicts: Dicts;
    dictsLoading: boolean;

    coverGroups: DropDown[];
    coverGroupsLoading: boolean;

    proceduresDef: DropDown[];
    proceduresDefLoading: boolean;

    parentService: DropDown[];
    parentServiceLoading: boolean;

    service: DropDown[];
    serviceLoading: boolean;
};

const TariffInfoContext = createContext<TariffInfoContextType>({
    dicts: {
        termUnits: [],
        serviceTypes: [],
        calcTypes: [],
    },
    dictsLoading: false,
    coverGroups: [],
    coverGroupsLoading: false,
    proceduresDef: [],
    proceduresDefLoading: false,
    parentService: [],
    parentServiceLoading: false,
    service: [],
    serviceLoading: false,
});

const TariffInfoProvider = ({ children }: { children: ReactNode }) => {
    const api = useApi();
    const { i18n } = useTranslation();
    const { handleRequestError } = useErrorState();

    const [dicts, setDicts] = useState<Dicts>({
        termUnits: [],
        serviceTypes: [],
        calcTypes: [],
    });
    const [coverGroups, setCoverGroups] = useState<DropDown[]>([]);
    const [proceduresDef, setProceduresDef] = useState<DropDown[]>([]);
    const [parentService, setParentService] = useState<DropDown[]>([]);
    const [service, setService] = useState<DropDown[]>([]);

    const { isFetching: proceduresDefLoading } = useQuery(
        'proceduresDefList',
        () => getProceduresDefList(api),
        {
            onSuccess: (data) => {
                data && setProceduresDef(data);
            },
            onError: handleRequestError,
        }
    );

    const { isFetching: parentServiceLoading } = useQuery(
        'parentServiceList',
        () => getParentServiceList(api),
        {
            onSuccess: (data) => {
                data && setParentService(data);
            },
            onError: handleRequestError,
        }
    );

    const { isFetching: serviceLoading } = useQuery('serviceList', () => getServiceList(api), {
        onSuccess: (data) => {
            data && setService(data);
        },
        onError: handleRequestError,
    });

    const { isFetching: dictsLoading } = useQuery(
        'termUnitsDicts',
        () =>
            getSelectList(
                api,
                ['TermUnits', 'TariffPlanServiceType', 'TariffPlanCalcType'],
                i18n.language
            ),
        {
            onSuccess: (data) =>
                data['TermUnits'] &&
                setDicts({
                    termUnits: data['TermUnits'] ?? [],
                    serviceTypes: data['TariffPlanServiceType'] ?? [],
                    calcTypes: data['TariffPlanCalcType'] ?? [],
                }),
            onError: handleRequestError,
        }
    );

    const { isFetching: coverGroupsLoading } = useQuery(
        'coverGroupList',
        () => getCoverGroupList(api),
        {
            onSuccess: (data) => data && setCoverGroups(data),
            onError: handleRequestError,
        }
    );

    return (
        <TariffInfoContext.Provider
            value={{
                dicts,
                dictsLoading,
                coverGroups,
                coverGroupsLoading,
                proceduresDef,
                proceduresDefLoading,
                parentService,
                parentServiceLoading,
                service,
                serviceLoading,
            }}
        >
            {children}
        </TariffInfoContext.Provider>
    );
};

const useTariffInfoState = () => {
    const context = useContext(TariffInfoContext);

    return context;
};

export { TariffInfoProvider, useTariffInfoState };
